import type { ReactNode } from 'react'

import { useCopilotContext } from '@copilotkit/react-core'

import { useAiChat } from '@factorialco/f0-react'
import type { DashboardCanvasContent } from '@factorialco/f0-react'

import { defineCanvasEntity } from 'modules/ai/copilotActions/canvas/defineCanvasEntity'

import { DashboardContent } from 'modules/ai/copilotActions/canvas/dashboards/components/DashboardContent'
import { DashboardHeader } from 'modules/ai/copilotActions/canvas/dashboards/components/DashboardHeader'
import { DashboardProvider } from 'modules/ai/copilotActions/canvas/dashboards/components/DashboardProvider'

/**
 * Wrapper for the dashboard entity. Lives as a named component
 * (uppercase) so the AI / CopilotKit hooks satisfy
 * `react-hooks/rules-of-hooks`. The underlying provider receives
 * everything it needs as props and stays decoupled from `useAiChat`.
 */
const DashboardCanvasWrapper = ({
  content,
  children,
}: {
  content: DashboardCanvasContent
  children: ReactNode
}) => {
  const { canvasActions, openCanvas, pendingContext, setPendingContext } = useAiChat()
  const { threadId } = useCopilotContext()
  return (
    <DashboardProvider
      content={content}
      canvasActions={canvasActions}
      onCanvasContentChange={openCanvas}
      pendingContext={pendingContext}
      setPendingContext={setPendingContext}
      threadId={threadId}
    >
      {children}
    </DashboardProvider>
  )
}

/**
 * Canvas renderer for `{ type: "dashboard" }` content. Wraps the body in
 * `DashboardProvider` so the header (save/edit) and content (compute +
 * grid) share state. Dashboard manages its own scroll: the grid grows
 * with `flex-1` so a single-item dashboard fills the height (no scroll),
 * while a multi-item dashboard scrolls internally above the persistent
 * action bar.
 */
export const dashboardCanvasEntity = defineCanvasEntity<DashboardCanvasContent>({
  type: 'dashboard',
  renderContent: ({ content, refreshKey }) => (
    <DashboardContent content={content} refreshKey={refreshKey} />
  ),
  renderHeader: ({ content, onClose }) => <DashboardHeader content={content} onClose={onClose} />,
  wrapper: ({ content, children }) => (
    <DashboardCanvasWrapper content={content}>{children}</DashboardCanvasWrapper>
  ),
  overflowHidden: true,
})
